import React, { useEffect, useState } from 'react';
import { Save, Check } from 'lucide-react';

const tok = () => localStorage.getItem('accessToken') || '';
const api = (path: string, opts?: RequestInit) =>
  fetch(`/api/ryan${path}`, { ...opts, headers: { Authorization: `Bearer ${tok()}`, 'Content-Type': 'application/json', ...(opts?.headers || {}) } });

function formatPrice(cents: number): string {
  return `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`;
}

const DISCOUNT_PRESETS = [10, 20, 30];

interface Suggestion {
  _id: string;
  title: string;
  priceCents: number;
}

interface ExistingRecommendation {
  _id: string;
  recommendedProductId: { _id: string; title: string; priceCents: number };
  priceCents: number | null;
  description: string;
  highlights: string[];
  badge: string;
  active: boolean;
}

interface Props {
  productId: string;
  productTitle: string;
  productPriceCents: number;
}

// Configuração de recomendações ESPECÍFICA do produto sendo editado (cada
// produto tem seu próprio conjunto de recomendações, ou nenhuma). Sugere
// automaticamente produtos ativos do mesmo período — produtos de períodos
// diferentes não fazem sentido pedagógico recomendados juntos. Cada
// recomendação marcada é INDEPENDENTE das outras: pode ter seu próprio
// desconto (ou nenhum), descrição, destaques e selo, e no checkout pode ser
// aceita/recusada separadamente.
const RyanAdminProductUpsell: React.FC<Props> = ({ productId, productTitle, productPriceCents }) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [priceInputs, setPriceInputs] = useState<Record<string, string>>({});
  const [descInputs, setDescInputs] = useState<Record<string, string>>({});
  const [highlightsInputs, setHighlightsInputs] = useState<Record<string, string>>({});
  const [badgeInputs, setBadgeInputs] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    setError(null);
    Promise.all([
      api(`/admin/products/${productId}/upsell-suggestions`),
      api(`/admin/products/${productId}/upsell-offer`),
    ]).then(async ([suggestionsRes, recsRes]) => {
      if (suggestionsRes.ok) setSuggestions(await suggestionsRes.json());
      if (recsRes.ok) {
        const recs: ExistingRecommendation[] = await recsRes.json();
        setSelectedIds(new Set(recs.map(r => r.recommendedProductId._id)));
        const prices: Record<string, string> = {};
        const descs: Record<string, string> = {};
        const highlights: Record<string, string> = {};
        const badges: Record<string, string> = {};
        for (const r of recs) {
          const id = r.recommendedProductId._id;
          if (r.priceCents !== null && r.priceCents !== undefined) {
            prices[id] = (r.priceCents / 100).toFixed(2);
          }
          descs[id] = r.description || '';
          highlights[id] = (r.highlights || []).join('\n');
          badges[id] = r.badge || '';
        }
        setPriceInputs(prices);
        setDescInputs(descs);
        setHighlightsInputs(highlights);
        setBadgeInputs(badges);
      } else {
        setSelectedIds(new Set());
        setPriceInputs({});
        setDescInputs({});
        setHighlightsInputs({});
        setBadgeInputs({});
      }
    }).finally(() => setLoading(false));
  }, [productId]);

  const toggleSuggestion = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setSaved(false);
  };

  const setPriceInput = (id: string, value: string) => {
    setPriceInputs(prev => ({ ...prev, [id]: value }));
    setSaved(false);
  };

  const applyDiscount = (s: Suggestion, pct: number) => {
    const discounted = Math.round(s.priceCents * (1 - pct / 100));
    setPriceInput(s._id, (discounted / 100).toFixed(2));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const recommendations = Array.from(selectedIds).map(id => {
        const raw = priceInputs[id];
        const priceCents = raw && raw.trim() !== ''
          ? Math.round(Number(raw.replace(',', '.')) * 100)
          : null;
        const highlights = (highlightsInputs[id] || '')
          .split('\n').map(h => h.trim()).filter(Boolean);
        return {
          productId: id,
          priceCents,
          description: (descInputs[id] || '').trim(),
          highlights,
          badge: (badgeInputs[id] || '').trim(),
        };
      });

      const res = await api(`/admin/products/${productId}/upsell-offer`, {
        method: 'PUT',
        body: JSON.stringify({ recommendations }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({} as { message?: string }));
        throw new Error(data.message || 'Erro ao salvar recomendações.');
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro de conexão.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="rap-hint">Carregando…</p>;

  if (suggestions.length === 0) {
    return (
      <p className="rap-hint">
        Nenhum outro produto ativo no mesmo período de "{productTitle}" pra recomendar ainda.
      </p>
    );
  }

  return (
    <>
      <p className="rap-hint">
        Produtos ativos do mesmo período de "{productTitle}". Marque quantos quiser recomendar — cada um
        é independente e pode ter seu próprio desconto, descrição, destaques e selo (ou nenhum desses).
      </p>

      <div className="rapu-list">
        {suggestions.map(s => {
          const isSelected = selectedIds.has(s._id);
          const priceInput = priceInputs[s._id] || '';
          const effectiveCents = priceInput.trim() !== ''
            ? Math.round(Number(priceInput.replace(',', '.')) * 100)
            : s.priceCents;
          const hasDiscount = priceInput.trim() !== '' && effectiveCents < s.priceCents;

          return (
            <div key={s._id} className={`rapu-row${isSelected ? ' rapu-row--selected' : ''}`}>
              <button type="button" className="rapu-row-toggle" onClick={() => toggleSuggestion(s._id)}>
                <span className={`rapu-checkbox${isSelected ? ' checked' : ''}`}>
                  {isSelected && <Check size={12} />}
                </span>
                <span className="rapu-row-title">{s.title}</span>
                <span className="rapu-row-price">
                  {hasDiscount && <span className="rapu-row-price-from">{formatPrice(s.priceCents)}</span>}
                  {formatPrice(effectiveCents)}
                </span>
              </button>

              {isSelected && (
                <div className="rapu-row-config">
                  <div className="rap-field-row">
                    {DISCOUNT_PRESETS.map(pct => (
                      <button key={pct} type="button" className="rap-btn-ghost"
                        onClick={() => applyDiscount(s, pct)}>
                        {pct}% off
                      </button>
                    ))}
                    <button type="button" className="rap-btn-ghost" onClick={() => setPriceInput(s._id, '')}>
                      Sem desconto
                    </button>
                  </div>
                  <div className="rap-field">
                    <label className="rap-label">Preço promocional (R$) — vazio = sem desconto</label>
                    <input className="rap-input" value={priceInput}
                      onChange={e => setPriceInput(s._id, e.target.value)}
                      placeholder={(s.priceCents / 100).toFixed(2)} />
                  </div>

                  <div className="rap-field">
                    <label className="rap-label">Selo (opcional) — ex: "Mais escolhido", "Recomendação da Graia"</label>
                    <input className="rap-input" value={badgeInputs[s._id] || ''}
                      onChange={e => setBadgeInputs(prev => ({ ...prev, [s._id]: e.target.value }))}
                      placeholder="Deixe vazio pra não mostrar nenhum selo" />
                  </div>

                  <div className="rap-field">
                    <label className="rap-label">Descrição curta (opcional)</label>
                    <input className="rap-input" value={descInputs[s._id] || ''}
                      onChange={e => setDescInputs(prev => ({ ...prev, [s._id]: e.target.value }))}
                      placeholder="Uma frase sobre esse material" />
                  </div>

                  <div className="rap-field">
                    <label className="rap-label">Destaques (opcional) — um por linha, ex: "+300 flashcards"</label>
                    <textarea className="rap-textarea" rows={3} value={highlightsInputs[s._id] || ''}
                      onChange={e => setHighlightsInputs(prev => ({ ...prev, [s._id]: e.target.value }))}
                      placeholder={'+300 flashcards\n4 apostilas\nIdeal para o ciclo básico'} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {error && <p className="rap-upsell-error">{error}</p>}

      <div className="rap-upsell-form-actions">
        <button type="button" className="rap-btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={14} /> {saved ? 'Salvo!' : saving ? 'Salvando…' : 'Salvar recomendações'}
        </button>
      </div>
    </>
  );
};

export default RyanAdminProductUpsell;
